'use client';

import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

import type { HTMLTagProps } from '../../../types';

const chipVariants = cva(
  [
    'box-border flex cursor-pointer items-center rounded-pill border-1 px-12 py-6 font-500 transition-colors typo-body4',
    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-B-500/50 has-[:focus-visible]:ring-offset-2',
    'has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
  ],
  {
    variants: {
      theme: {
        primary:
          'border-Gray-300 bg-Gray-white text-Gray-700 hover:border-Gray-400 hover:bg-Gray-50 active:border-Gray-500 active:bg-Gray-100',
        'primary-line':
          'border-Gray-300 bg-Gray-white text-Gray-700 hover:border-Gray-400 hover:bg-Gray-50 active:border-Gray-500 active:bg-Gray-100',
        sub: 'border-Gray-300 bg-Gray-300 text-Gray-700 hover:border-Gray-400 hover:bg-Gray-400 active:border-Gray-500 active:bg-Gray-500',
      },
      checked: {
        // compoundVariants에서 처리하므로 빈문자열 클래스 할당
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        theme: 'primary',
        checked: true,
        className:
          'border-Gray-900 bg-Gray-900 text-Gray-white hover:border-Gray-800 hover:bg-Gray-800 active:border-Gray-700 active:bg-Gray-700',
      },
      {
        theme: 'primary-line',
        checked: true,
        className:
          'border-Gray-900 text-Gray-900 hover:border-Gray-800 hover:text-Gray-800 active:border-Gray-700 active:text-Gray-700',
      },
      {
        theme: 'sub',
        checked: true,
        className:
          'border-Gray-900 bg-Gray-900 text-Gray-white hover:border-Gray-800 hover:bg-Gray-800 active:border-Gray-700 active:bg-Gray-700',
      },
    ],
    defaultVariants: {
      theme: 'primary',
      checked: false,
    },
  },
);

export interface ChipProps
  extends
    Omit<HTMLTagProps<'input'>, 'checked'>,
    Omit<VariantProps<typeof chipVariants>, 'checked'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const Chip = forwardRef<HTMLInputElement, ChipProps>(
  (
    {
      theme,
      children,
      onChange,
      onCheckedChange,
      className,
      checked,
      type = 'checkbox',
      ...restProps
    }: ChipProps,
    ref,
  ) => {
    return (
      <label className={cn(chipVariants({ theme, checked, className }))}>
        <input
          {...restProps}
          ref={ref}
          className="sr-only"
          type={type}
          checked={checked}
          onChange={(e) => {
            onChange?.(e);
            onCheckedChange?.(e.target.checked);
          }}
        />
        {children}
      </label>
    );
  },
);

Chip.displayName = 'Chip';
